import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { Visibility, Add, Download, Description, History, CalendarMonth } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { noticeAPI, dashboardAPI, exportAPI } from '../services/api';
import { format } from 'date-fns';

interface Notice {
  id: string;
  notice_number: string;
  status: string;
  min_sell_price: number;
  share_quantity_total: number;
  start_date: string;
  end_date: string;
  created_at: string;
}

interface DashboardData {
  capitalRaisedThisMonth: number;
  capitalRaisedYTD: number;
  activeNotice: Notice | null;
  pendingNoticesCount: number;
}

const Dashboard: React.FC = () => {
  const { user, isAdmin, isNovus } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [notices, setNotices] = useState<Notice[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Dialog states
  const [activateDialog, setActivateDialog] = useState<{ open: boolean; notice: Notice | null }>({ open: false, notice: null });
  const [cancelDialog, setCancelDialog] = useState<{ open: boolean; notice: Notice | null }>({ open: false, notice: null });
  const [completeDialog, setCompleteDialog] = useState<{ open: boolean; notice: Notice | null }>({ open: false, notice: null });
  const [cancelReason, setCancelReason] = useState('');
  const [completeData, setCompleteData] = useState({ shares_sold: '', average_sell_price: '' });
  const [actionLoading, setActionLoading] = useState(false);
  
  // Export states
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [monthlyReportDialog, setMonthlyReportDialog] = useState(false);
  const [reportMonth, setReportMonth] = useState(new Date().getMonth() + 1);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    // Check for success message from navigation
    if (location.state?.success) {
      setSuccess(location.state.success);
      // Clear the state
      window.history.replaceState({}, document.title);
    }
    loadData();
  }, [location.state]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [dashboard, allNotices] = await Promise.all([
        dashboardAPI.getSummary(),
        noticeAPI.getAll()
      ]);
      setDashboardData(dashboard);
      setNotices(allNotices);
    } catch (err: any) {
      setError('Failed to load dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    if (!activateDialog.notice) return;
    setActionLoading(true);
    try {
      await noticeAPI.activate(activateDialog.notice.id);
      setSuccess('Notice activated successfully!');
      setActivateDialog({ open: false, notice: null });
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to activate notice');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelDialog.notice) return;
    setActionLoading(true);
    try {
      await noticeAPI.cancel(cancelDialog.notice.id, cancelReason);
      setSuccess('Notice cancelled successfully!');
      setCancelDialog({ open: false, notice: null });
      setCancelReason('');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to cancel notice');
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!completeDialog.notice) return;
    setActionLoading(true);
    try {
      await noticeAPI.complete(completeDialog.notice.id, {
        shares_sold: parseInt(completeData.shares_sold),
        average_sell_price: completeData.average_sell_price 
          ? parseFloat(completeData.average_sell_price) 
          : undefined
      });
      setSuccess('Notice completed successfully!');
      setCompleteDialog({ open: false, notice: null });
      setCompleteData({ shares_sold: '', average_sell_price: '' });
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to complete notice');
    } finally {
      setActionLoading(false);
    }
  };

  // Export handlers
  const handleExportNotices = async () => {
    try {
      setExporting(true);
      setExportMenuAnchor(null);
      await exportAPI.downloadNotices({ status: statusFilter || undefined });
      setSuccess('Notices exported successfully');
    } catch (err: any) {
      setError('Failed to export notices');
    } finally {
      setExporting(false);
    }
  };

  const handleExportAudit = async () => {
    try {
      setExporting(true);
      setExportMenuAnchor(null);
      await exportAPI.downloadAudit();
      setSuccess('Audit trail exported successfully');
    } catch (err: any) {
      setError('Failed to export audit trail');
    } finally {
      setExporting(false);
    }
  };

  const handleExportMonthlyReport = async () => {
    try {
      setExporting(true);
      setMonthlyReportDialog(false);
      await exportAPI.downloadMonthlyReport(reportYear, reportMonth);
      setSuccess('Monthly report exported successfully');
    } catch (err: any) {
      setError('Failed to export monthly report');
    } finally {
      setExporting(false);
    }
  };

  const getStatusColor = (status: string): 'warning' | 'success' | 'info' | 'error' | 'default' => {
    switch (status) {
      case 'pending': return 'warning';
      case 'active': return 'success';
      case 'completed': return 'info';
      case 'expired': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  const filteredNotices = statusFilter 
    ? notices.filter(n => n.status === statusFilter)
    : notices;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            {isNovus ? 'Novus Capital Portal' : 'ATM Facility Dashboard'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Welcome back, {user?.first_name} {user?.last_name}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {!isNovus && (
            <>
              <Button 
                variant="outlined" 
                startIcon={exporting ? <CircularProgress size={20} /> : <Download />}
                onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                disabled={exporting}
              >
                Export
              </Button>
              <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={() => setExportMenuAnchor(null)}
              >
                <MenuItem onClick={handleExportNotices}>
                  <ListItemIcon><Description fontSize="small" /></ListItemIcon>
                  <ListItemText>Export Notices (CSV)</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleExportAudit}>
                  <ListItemIcon><History fontSize="small" /></ListItemIcon>
                  <ListItemText>Export Audit Trail (CSV)</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { setExportMenuAnchor(null); setMonthlyReportDialog(true); }}>
                  <ListItemIcon><CalendarMonth fontSize="small" /></ListItemIcon>
                  <ListItemText>Monthly Report</ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
          {isAdmin && (
            <Button 
              variant="contained" 
              startIcon={<Add />}
              onClick={() => navigate('/notices/new')}
            >
              Create Notice
            </Button>
          )}
        </Box>
      </Box>

      {/* Alerts */}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* KPI Cards */}
      {!isNovus && dashboardData && (
        <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Capital Raised This Month
                </Typography>
                <Typography variant="h5" color="primary">
                  {formatCurrency(dashboardData.capitalRaisedThisMonth)}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Capital Raised YTD
                </Typography>
                <Typography variant="h5" color="primary">
                  {formatCurrency(dashboardData.capitalRaisedYTD)}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Active Notices
                </Typography>
                <Typography variant="h5">
                  {dashboardData.activeNotice ? 1 : 0}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Pending Notices
                </Typography>
                <Typography variant="h5">
                  {dashboardData.pendingNoticesCount}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Active Notice */}
      {dashboardData?.activeNotice && (
        <Card sx={{ mb: 4, bgcolor: 'success.main', color: 'white' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography variant="overline">Active Notice</Typography>
                <Typography variant="h5">
                  {dashboardData.activeNotice.notice_number}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ color: 'white', borderColor: 'white' }}
                  onClick={() => navigate(`/notices/${dashboardData.activeNotice!.id}`)}
                >
                  View Details
                </Button>
                {isAdmin && (
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={() => setCancelDialog({ open: true, notice: dashboardData.activeNotice! })}
                  >
                    Cancel
                  </Button>
                )}
                {isNovus && (
                  <Button
                    variant="contained"
                    color="info"
                    size="small"
                    onClick={() => setCompleteDialog({ open: true, notice: dashboardData.activeNotice! })}
                  >
                    Complete
                  </Button>
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>Min Sell Price</Typography>
                <Typography variant="h6">{formatCurrency(dashboardData.activeNotice.min_sell_price)}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>Shares Authorized</Typography>
                <Typography variant="h6">{dashboardData.activeNotice.share_quantity_total.toLocaleString()}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>Period</Typography>
                <Typography variant="h6">
                  {format(new Date(dashboardData.activeNotice.start_date), 'MMM d')} - {format(new Date(dashboardData.activeNotice.end_date), 'MMM d, yyyy')}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* No Active Notice Alert for Novus */}
      {isNovus && !dashboardData?.activeNotice && (
        <Alert severity="info" sx={{ mb: 4 }}>
          No active notice at the moment. You will be notified when a new notice is activated.
        </Alert>
      )}

      {/* Notices Table */}
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">All Notices</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Filter by Status</InputLabel>
              <Select
                value={statusFilter}
                label="Filter by Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Notice Number</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Min Price</TableCell>
                <TableCell>Shares</TableCell>
                <TableCell>Period</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredNotices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No notices found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredNotices.map((notice) => (
                  <TableRow 
                    key={notice.id} 
                    hover 
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/notices/${notice.id}`)}
                  >
                    <TableCell>
                      <Typography fontWeight="medium">{notice.notice_number}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={notice.status.toUpperCase()}
                        color={getStatusColor(notice.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatCurrency(notice.min_sell_price)}</TableCell>
                    <TableCell>{notice.share_quantity_total.toLocaleString()}</TableCell>
                    <TableCell>
                      {format(new Date(notice.start_date), 'MMM d')} - {format(new Date(notice.end_date), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => navigate(`/notices/${notice.id}`)}>
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        {isAdmin && notice.status === 'pending' && (
                          <>
                            <Button 
                              size="small" 
                              color="success"
                              onClick={() => setActivateDialog({ open: true, notice })}
                            >
                              Activate
                            </Button>
                            <Button 
                              size="small" 
                              color="error"
                              onClick={() => setCancelDialog({ open: true, notice })}
                            >
                              Cancel
                            </Button>
                          </>
                        )}
                        {isAdmin && notice.status === 'active' && (
                          <Button 
                            size="small" 
                            color="error"
                            onClick={() => setCancelDialog({ open: true, notice })}
                          >
                            Cancel
                          </Button>
                        )}
                        {isNovus && notice.status === 'active' && (
                          <Button 
                            size="small" 
                            color="primary"
                            onClick={() => setCompleteDialog({ open: true, notice })}
                          >
                            Complete
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Activate Dialog */}
      <Dialog open={activateDialog.open} onClose={() => setActivateDialog({ open: false, notice: null })}>
        <DialogTitle>Activate Notice</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to activate notice <strong>{activateDialog.notice?.notice_number}</strong>?
          </DialogContentText>
          {activateDialog.notice && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Min Sell Price:</strong> {formatCurrency(activateDialog.notice.min_sell_price)}
              </Typography>
              <Typography variant="body2">
                <strong>Shares Authorized:</strong> {activateDialog.notice.share_quantity_total.toLocaleString()}
              </Typography>
              <Typography variant="body2">
                <strong>Period:</strong> {activateDialog.notice.start_date} to {activateDialog.notice.end_date}
              </Typography>
            </Box>
          )}
          <DialogContentText sx={{ mt: 2 }}>
            This will authorize Novus Capital to begin selling shares under this notice.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateDialog({ open: false, notice: null })} disabled={actionLoading}>
            Cancel
          </Button>
          <Button onClick={handleActivate} variant="contained" color="success" disabled={actionLoading}>
            {actionLoading ? 'Activating...' : 'Activate Notice'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialog.open} onClose={() => setCancelDialog({ open: false, notice: null })}>
        <DialogTitle>Cancel Notice</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel notice <strong>{cancelDialog.notice?.notice_number}</strong>?
          </DialogContentText>
          <DialogContentText sx={{ mt: 1, color: 'warning.main' }}>
            This action cannot be undone.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Cancellation Reason (Optional)"
            fullWidth
            multiline
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            sx={{ mt: 2 }}
            placeholder="Enter reason for cancellation..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCancelDialog({ open: false, notice: null }); setCancelReason(''); }} disabled={actionLoading}>
            Keep Notice
          </Button>
          <Button onClick={handleCancel} variant="contained" color="error" disabled={actionLoading}>
            {actionLoading ? 'Cancelling...' : 'Cancel Notice'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete Dialog */}
      <Dialog open={completeDialog.open} onClose={() => setCompleteDialog({ open: false, notice: null })}>
        <DialogTitle>Complete Notice</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter the share sale details to complete notice <strong>{completeDialog.notice?.notice_number}</strong>.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Shares Sold"
            type="number"
            fullWidth
            required
            value={completeData.shares_sold}
            onChange={(e) => setCompleteData(prev => ({ ...prev, shares_sold: e.target.value }))}
            inputProps={{ min: 1, max: completeDialog.notice?.share_quantity_total }}
            helperText={completeDialog.notice ? `Maximum: ${completeDialog.notice.share_quantity_total.toLocaleString()}` : ''}
            sx={{ mt: 2 }}
          />
          <TextField
            margin="dense"
            label="Average Sell Price (Optional)"
            type="number"
            fullWidth
            value={completeData.average_sell_price}
            onChange={(e) => setCompleteData(prev => ({ ...prev, average_sell_price: e.target.value }))}
            inputProps={{ min: completeDialog.notice?.min_sell_price, step: '0.0001' }}
            helperText={completeDialog.notice ? `Minimum: ${formatCurrency(completeDialog.notice.min_sell_price)}` : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCompleteDialog({ open: false, notice: null }); setCompleteData({ shares_sold: '', average_sell_price: '' }); }} disabled={actionLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleComplete} 
            variant="contained" 
            disabled={actionLoading || !completeData.shares_sold}
          >
            {actionLoading ? 'Completing...' : 'Complete Notice'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Monthly Report Dialog */}
      <Dialog open={monthlyReportDialog} onClose={() => setMonthlyReportDialog(false)}>
        <DialogTitle>Generate Monthly Report</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Select the month and year for the report.
          </DialogContentText>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Month</InputLabel>
              <Select
                value={reportMonth}
                label="Month"
                onChange={(e) => setReportMonth(Number(e.target.value))}
              >
                <MenuItem value={1}>January</MenuItem>
                <MenuItem value={2}>February</MenuItem>
                <MenuItem value={3}>March</MenuItem>
                <MenuItem value={4}>April</MenuItem>
                <MenuItem value={5}>May</MenuItem>
                <MenuItem value={6}>June</MenuItem>
                <MenuItem value={7}>July</MenuItem>
                <MenuItem value={8}>August</MenuItem>
                <MenuItem value={9}>September</MenuItem>
                <MenuItem value={10}>October</MenuItem>
                <MenuItem value={11}>November</MenuItem>
                <MenuItem value={12}>December</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 100 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={reportYear}
                label="Year"
                onChange={(e) => setReportYear(Number(e.target.value))}
              >
                {[2023, 2024, 2025, 2026].map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMonthlyReportDialog(false)}>Cancel</Button>
          <Button onClick={handleExportMonthlyReport} variant="contained" disabled={exporting}>
            {exporting ? 'Generating...' : 'Generate Report'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Dashboard;
